import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NativeHaptics } from 'src/app/plugins/native-runtime.plugins';

export type HapticCue = 'selection' | 'action' | 'confirm' | 'success' | 'warning' | 'error';

/**
 * Laniakea's haptic vocabulary.
 *
 * Selection = a tiny tick, action = a light pulse, confirm = a firmer pulse,
 * and completed outcomes use the native success/warning/error patterns.
 */
@Injectable({ providedIn: 'root' })
export class HapticLanguageService {
  private globalFeedbackInstalled = false;
  private lastGlobalFeedbackAt = 0;

  private readonly actionableSelector = [
    '[data-haptic]',
    'button',
    'a[href]',
    'summary',
    'label[for]',
    'input[type="button"]',
    'input[type="submit"]',
    'input[type="reset"]',
    'input[type="checkbox"]',
    'input[type="radio"]',
    'input[type="file"]',
    'select',
    'ion-button',
    'ion-buttons',
    'ion-back-button',
    'ion-fab',
    'ion-fab-button',
    'ion-tab-button',
    'ion-segment-button',
    'ion-radio',
    'ion-checkbox',
    'ion-toggle',
    'ion-select',
    'ion-item',
    'ion-item-option',
    'ion-chip',
    'ion-accordion',
    'ion-menu-toggle',
    'ion-card[button]',
    '[role="button"]',
    '[role="tab"]',
    '[role="radio"]',
    '[role="switch"]',
    '[routerlink]',
    '[data-action]',
    '[tabindex]:not([tabindex="-1"])',
    '.clickable',
  ].join(',');

  private readonly selectionSelector = [
    'input[type="checkbox"]',
    'input[type="radio"]',
    'select',
    'ion-tab-button',
    'ion-segment-button',
    'ion-radio',
    'ion-checkbox',
    'ion-toggle',
    'ion-select',
    '[role="tab"]',
    '[role="radio"]',
    '[role="switch"]',
  ].join(',');

  private readonly globalRangeListener = (event: Event): void => {
    if (!event.isTrusted) return;

    const origin = event.target instanceof Element ? event.target : null;
    if (!origin?.matches('ion-range, input[type="range"]')) return;

    if (!this.claimGlobalFeedback(45)) return;
    void this.selection();
  };

  private readonly globalSlideListener = (): void => {
    if (typeof document === 'undefined' || document.hidden) return;

    if (!this.claimGlobalFeedback()) return;
    void this.selection();
  };

  private readonly globalFocusListener = (event: Event): void => {
    if (!event.isTrusted) return;

    const focusedControl = this.eventElements(event).find(element => element.matches(
      'input:not([type="button"]):not([type="submit"]):not([type="reset"]), textarea, ion-input, ion-textarea, ion-searchbar, [contenteditable="true"]'
    ));
    if (!focusedControl || !this.claimGlobalFeedback()) return;
    void this.selection();
  };

  private readonly globalSubmitListener = (event: Event): void => {
    if (!event.isTrusted || !this.claimGlobalFeedback(90)) return;
    void this.confirm();
  };

  private readonly globalDisabledControlListener = (event: Event): void => {
    const origin = event.target instanceof Element ? event.target : null;
    const control = origin ? this.findInteractiveControl(event, origin) : null;
    if (!control || !this.isDisabled(control)) return;

    if (!this.claimGlobalFeedback()) return;
    void this.warning();
  };

  private readonly globalClickListener = (event: Event): void => {
    if (!(event instanceof MouseEvent) || !event.isTrusted) return;

    const origin = event.target instanceof Element ? event.target : null;
    if (!origin) return;

    const control = this.findInteractiveControl(event, origin);
    if (!control) return;

    const explicitCue = this.findExplicitCue(event, origin);
    if (explicitCue === 'none') return;
    if (!this.claimGlobalFeedback()) return;

    if (this.isDisabled(control)) {
      void this.warning();
      return;
    }

    if (this.isCue(explicitCue)) {
      void this.play(explicitCue);
      return;
    }

    if (control.matches(this.selectionSelector)) {
      void this.selection();
      return;
    }

    const destructive = control.matches('[color="danger"], .danger, .destructive, [data-destructive="true"]');
    void (destructive ? this.confirm() : this.action());
  };

  installGlobalFeedback(): void {
    if (!this.isSupported() || this.globalFeedbackInstalled || typeof document === 'undefined') return;
    document.addEventListener('click', this.globalClickListener, true);
    document.addEventListener('input', this.globalRangeListener, true);
    document.addEventListener('focusin', this.globalFocusListener, true);
    document.addEventListener('submit', this.globalSubmitListener, true);
    document.addEventListener('pointerup', this.globalDisabledControlListener, true);
    document.addEventListener('ionSlideDidChange', this.globalSlideListener, true);
    this.globalFeedbackInstalled = true;
  }

  removeGlobalFeedback(): void {
    if (!this.globalFeedbackInstalled || typeof document === 'undefined') return;
    document.removeEventListener('click', this.globalClickListener, true);
    document.removeEventListener('input', this.globalRangeListener, true);
    document.removeEventListener('focusin', this.globalFocusListener, true);
    document.removeEventListener('submit', this.globalSubmitListener, true);
    document.removeEventListener('pointerup', this.globalDisabledControlListener, true);
    document.removeEventListener('ionSlideDidChange', this.globalSlideListener, true);
    this.globalFeedbackInstalled = false;
  }

  async play(cue: HapticCue): Promise<void> {
    switch (cue) {
      case 'selection': return this.selection();
      case 'confirm': return this.confirm();
      case 'success': return this.success();
      case 'warning': return this.warning();
      case 'error': return this.error();
      default: return this.action();
    }
  }

  async selection(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.selection(), 8);
  }

  async action(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.impact({ style: 'LIGHT' }), 12);
  }

  async confirm(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.impact({ style: 'MEDIUM' }), 28);
  }

  async success(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.notification({ type: 'SUCCESS' }), [12, 45, 18]);
  }

  async warning(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.notification({ type: 'WARNING' }), [18, 55, 18]);
  }

  async error(): Promise<void> {
    await this.nativeCall(() => NativeHaptics.notification({ type: 'ERROR' }), [28, 45, 28]);
  }

  private isSupported(): boolean {
    return Capacitor.isNativePlatform();
  }

  private async nativeCall(
    operation: () => Promise<void>,
    androidPattern: number | number[],
  ): Promise<void> {
    if (!this.isSupported()) return;

    if (Capacitor.getPlatform() === 'android') {
      try {
        navigator.vibrate?.(androidPattern);
      } catch (_) {}
      return;
    }

    try {
      await operation();
    } catch (_) {
      // Haptics are progressive enhancement and never alter the user action.
    }
  }

  private isDisabled(control: HTMLElement): boolean {
    return control.hasAttribute('disabled')
      || control.getAttribute('aria-disabled') === 'true'
      || control.classList.contains('ion-disabled')
      || (control as HTMLButtonElement).disabled === true;
  }

  private isCue(value?: string): value is HapticCue {
    return value === 'selection'
      || value === 'action'
      || value === 'confirm'
      || value === 'success'
      || value === 'warning'
      || value === 'error';
  }

  private findInteractiveControl(event: Event, origin: Element): HTMLElement | null {
    const nearestKnownControl = origin.closest(this.actionableSelector);
    if (nearestKnownControl instanceof HTMLElement) return nearestKnownControl;

    // Angular/Zone stores listeners on the element that owns `(click)`. This
    // catches custom divs, icons, images, labels, sections and app components
    // without adding haptic attributes to hundreds of templates. The cursor
    // and native onclick checks retain coverage if Zone internals change.
    return this.eventElements(event).find(element =>
      element.matches(this.actionableSelector)
      || this.hasRegisteredClickListener(element)
      || typeof element.onclick === 'function'
      || this.hasPointerCursor(element)
    ) ?? null;
  }

  private findExplicitCue(event: Event, origin: Element): string | undefined {
    const explicitElement = origin.closest('[data-haptic]')
      ?? this.eventElements(event).find(element => element.hasAttribute('data-haptic'));
    return explicitElement instanceof HTMLElement
      ? explicitElement.dataset?.haptic?.toLowerCase()
      : undefined;
  }

  private eventElements(event: Event): HTMLElement[] {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    return path.filter((entry): entry is HTMLElement => entry instanceof HTMLElement);
  }

  private hasRegisteredClickListener(element: HTMLElement): boolean {
    const zone = (globalThis as any).Zone;
    if (typeof zone?.__symbol__ !== 'function') return false;

    return ['clickfalse', 'clicktrue'].some(name => {
      const tasks = (element as any)[zone.__symbol__(name)];
      return Array.isArray(tasks)
        ? tasks.some(task => task && !task.isRemoved)
        : !!tasks;
    });
  }

  private hasPointerCursor(element: HTMLElement): boolean {
    try {
      return getComputedStyle(element).cursor === 'pointer';
    } catch (_) {
      return false;
    }
  }

  private claimGlobalFeedback(minimumGapMs = 55): boolean {
    const now = performance.now();
    if (now - this.lastGlobalFeedbackAt < minimumGapMs) return false;
    this.lastGlobalFeedbackAt = now;
    return true;
  }

}
